import React, { FC, useMemo, useState } from 'react';
import cx from 'classnames';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { flattenItems, ChildItem, OrderItem } from '../AdjustTableDialog.utils';
import { useAdjustTableDrag } from '../hooks/useAdjustTableDrag';
import SortableItem from './SortableItem';
import styles from '../AdjustTableDialog.module.css';

type ColumnReorderProps = {
  pinnedOrder: string[];
  unpinnedOrder: string[];
  itemMap: Map<string, OrderItem>;
  childOrderMap: Map<string, ChildItem[]>;
  setPinnedOrder: React.Dispatch<React.SetStateAction<string[]>>;
  setUnpinnedOrder: React.Dispatch<React.SetStateAction<string[]>>;
  setChildOrderMap: React.Dispatch<React.SetStateAction<Map<string, ChildItem[]>>>;
};

const ColumnReorder: FC<ColumnReorderProps> = ({
  pinnedOrder,
  unpinnedOrder,
  itemMap,
  childOrderMap,
  setPinnedOrder,
  setUnpinnedOrder,
  setChildOrderMap,
}: ColumnReorderProps) => {
  const flatPinnedItems = useMemo(
    () => flattenItems(pinnedOrder, itemMap, childOrderMap),
    [pinnedOrder, itemMap, childOrderMap],
  );

  const flatUnpinnedItems = useMemo(
    () => flattenItems(unpinnedOrder, itemMap, childOrderMap),
    [unpinnedOrder, itemMap, childOrderMap],
  );

  const {
    sensors,
    draggingSection,
    draggedItemId,
    isDropDisallowed,
    flatPinnedIds,
    flatUnpinnedIds,
    handlePinnedDragStart,
    handlePinnedDragEnd,
    handlePinnedDragOver,
    handleUnpinnedDragStart,
    handleUnpinnedDragEnd,
    handleUnpinnedDragOver,
    handleDragReset,
  } = useAdjustTableDrag({
    flatPinnedItems,
    flatUnpinnedItems,
    itemMap,
    setPinnedOrder,
    setUnpinnedOrder,
    setChildOrderMap,
  });

  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);

  return (
    <div className={styles.contentWrapper}>
      {flatPinnedItems.length > 0 && (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handlePinnedDragStart}
            onDragOver={handlePinnedDragOver}
            onDragEnd={handlePinnedDragEnd}
            onDragCancel={handleDragReset}
            autoScroll={false}
          >
            <SortableContext items={flatPinnedIds} strategy={verticalListSortingStrategy}>
              <ul
                className={cx(styles.columnList, styles.pinnedColumnList, {
                  [styles.dimmedSection]: draggingSection === 'unpinned',
                  [styles.dragDisallowed]: draggingSection === 'pinned' && isDropDisallowed,
                })}
                data-testid="adjust-table-pinned-list"
                onMouseLeave={() => setHoveredGroupId(null)}
              >
                {flatPinnedItems.map(flatItem => (
                  <SortableItem
                    key={flatItem.id}
                    item={flatItem}
                    isDropDisallowed={draggedItemId === flatItem.id && isDropDisallowed}
                    onHoverGroup={setHoveredGroupId}
                    isDimmed={
                      hoveredGroupId !== null && flatItem.parentId !== null && flatItem.parentId !== hoveredGroupId
                    }
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
          <div className={styles.sectionDivider} />
        </>
      )}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleUnpinnedDragStart}
        onDragOver={handleUnpinnedDragOver}
        onDragEnd={handleUnpinnedDragEnd}
        onDragCancel={handleDragReset}
        autoScroll={false}
      >
        <SortableContext items={flatUnpinnedIds} strategy={verticalListSortingStrategy}>
          <ul
            className={cx(styles.columnList, {
              [styles.dimmedSection]: draggingSection === 'pinned',
              [styles.dragDisallowed]: draggingSection === 'unpinned' && isDropDisallowed,
            })}
            data-testid="adjust-table-unpinned-list"
            onMouseLeave={() => setHoveredGroupId(null)}
          >
            {flatUnpinnedItems.map(flatItem => (
              <SortableItem
                key={flatItem.id}
                item={flatItem}
                isDropDisallowed={draggedItemId === flatItem.id && isDropDisallowed}
                onHoverGroup={setHoveredGroupId}
                isDimmed={hoveredGroupId !== null && flatItem.parentId !== null && flatItem.parentId !== hoveredGroupId}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </div>
  );
};

export default ColumnReorder;
